import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { canAccess } from '../../utils/adminPermissions';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import './AdminNotificationsDropdown.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';

async function apiFetch(path) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: { 'X-API-Key': API_KEY } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

/**
 * Admins aren't buyers or sellers, so nothing writes to a per-admin
 * notification inbox the way it does for them. This composes a feed
 * client-side instead, from admin endpoints that already exist — "3
 * pending payouts," "1 blocked payout" — rather than adding a new backend
 * feed just for this.
 */
const AdminNotificationsDropdown = ({ adminRole }) => {
  const [open, setOpen]   = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const checks = [];
    if (canAccess(adminRole, 'payouts')) {
      checks.push(
        apiFetch('/api/admin/payout-requests?status=pending')
          .then((d) => (d.requests || []).length)
          .then((n) => n > 0 && {
            key: 'payouts', to: '/dashboard/payouts',
            text: `${n} pending payout request${n !== 1 ? 's' : ''}`,
          })
          .catch(() => null)
      );
    }
    if (canAccess(adminRole, 'blockedPayouts')) {
      checks.push(
        apiFetch('/api/admin/payouts/blocked')
          .then((d) => (d.sellers || []).length)
          .then((n) => n > 0 && {
            key: 'blocked', to: '/dashboard/blocked-payouts',
            text: `${n} blocked payout${n !== 1 ? 's' : ''} need attention`,
          })
          .catch(() => null)
      );
    }
    if (canAccess(adminRole, 'escrow')) {
      checks.push(
        apiFetch('/api/admin/escrow')
          .then((d) => (d.frozen || []).length)
          .then((n) => n > 0 && {
            key: 'frozen', to: '/dashboard/escrow',
            text: `${n} order${n !== 1 ? 's' : ''} frozen in escrow`,
          })
          .catch(() => null)
      );
    }

    const results = await Promise.all(checks);
    setItems(results.filter(Boolean));
  }, [adminRole]);

  useAutoRefresh(load, { intervalMs: 60000 });

  const goTo = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div className="admin-notif-wrap">
      <button
        className="admin-notif-btn"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {items.length > 0 && <span className="admin-notif-badge">{items.length}</span>}
      </button>

      {open && (
        <>
          <div className="admin-notif-overlay" onClick={() => setOpen(false)} />
          <div className="admin-notif-panel">
            <div className="admin-notif-panel-title">Notifications</div>
            {items.length === 0 ? (
              <div className="admin-notif-empty">Nothing needs attention.</div>
            ) : (
              items.map((item) => (
                <button
                  key={item.key}
                  className="admin-notif-item"
                  onClick={() => goTo(item.to)}
                >
                  {item.text}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminNotificationsDropdown;
